import {
  PREVENTION_CONTROL_TYPES,
  type CloudProvider,
  type ConfigControlSpec,
  type PreventionControlSpec,
} from './prevention.schema';

export const CONFIG_CHANGE_SUPPORT = {
  SUPPORTED: 'supported',
  ADVISORY_ONLY: 'advisoryOnly',
  UNSUPPORTED: 'unsupported',
} as const;

export type ConfigChangeSupport =
  (typeof CONFIG_CHANGE_SUPPORT)[keyof typeof CONFIG_CHANGE_SUPPORT];

type ConfigChangeCapability = {
  support: ConfigChangeSupport;
  constraints: readonly string[];
};

const ADVISORY_CONSTRAINTS = [
  'This change type is advisory-only for the target cloud; generate a precise plan but expect manual implementation.',
] as const;

const CONFIG_CHANGE_MATRIX: Record<
  CloudProvider,
  Record<ConfigControlSpec['change'], ConfigChangeCapability>
> = {
  aws: {
    disablePublicAccess: {
      support: CONFIG_CHANGE_SUPPORT.SUPPORTED,
      constraints: [
        'Target must identify an S3 bucket (name or ARN) in details.bucketName or target.',
        'Use change disablePublicAccess only for public bucket or object exposure findings.',
      ],
    },
    enforceTls: {
      support: CONFIG_CHANGE_SUPPORT.SUPPORTED,
      constraints: [
        'Include listenerArn in details; enforcement sets the HTTPS listener SSL policy on that listener.',
        'Include certificateArn in details when known so the certificate is validated as ISSUED before the policy changes.',
      ],
    },
    dnsRecord: {
      support: CONFIG_CHANGE_SUPPORT.SUPPORTED,
      constraints: [
        'Include hostedZoneId, recordName, recordType, and recordValue in details; Route 53 enforcement upserts that exact record set.',
        'Use change dnsRecord only when the remedy is a Route 53 record in a hosted zone Terra can reach.',
      ],
    },
    emailAuth: {
      support: CONFIG_CHANGE_SUPPORT.SUPPORTED,
      constraints: [
        'Include hostedZoneId, recordName, and recordValue in details; the record is written as a Route 53 TXT record.',
        'Use change emailAuth for SPF or DMARC TXT records only.',
      ],
    },
  },
  gcp: {
    disablePublicAccess: {
      support: CONFIG_CHANGE_SUPPORT.SUPPORTED,
      constraints: [
        'Target must identify a Cloud Storage bucket (name or gs:// URI) in details.bucketName or target.',
        'Enforcement sets publicAccessPrevention to enforced and leaves uniform bucket-level access untouched, since enabling it breaks object ACLs and is only reversible for 90 days.',
      ],
    },
    enforceTls: {
      support: CONFIG_CHANGE_SUPPORT.ADVISORY_ONLY,
      constraints: ADVISORY_CONSTRAINTS,
    },
    dnsRecord: {
      support: CONFIG_CHANGE_SUPPORT.ADVISORY_ONLY,
      constraints: ADVISORY_CONSTRAINTS,
    },
    emailAuth: {
      support: CONFIG_CHANGE_SUPPORT.ADVISORY_ONLY,
      constraints: ADVISORY_CONSTRAINTS,
    },
  },
  azure: {
    disablePublicAccess: {
      support: CONFIG_CHANGE_SUPPORT.SUPPORTED,
      constraints: [
        'Include resourceGroup and accountName in details; subscriptionId falls back to the connected cloud integration.',
        'Enforcement sets allowBlobPublicAccess to false on the storage account.',
      ],
    },
    enforceTls: {
      support: CONFIG_CHANGE_SUPPORT.ADVISORY_ONLY,
      constraints: ADVISORY_CONSTRAINTS,
    },
    dnsRecord: {
      support: CONFIG_CHANGE_SUPPORT.ADVISORY_ONLY,
      constraints: ADVISORY_CONSTRAINTS,
    },
    emailAuth: {
      support: CONFIG_CHANGE_SUPPORT.ADVISORY_ONLY,
      constraints: ADVISORY_CONSTRAINTS,
    },
  },
};

export const resolveConfigChangeCapability = ({
  cloudProvider,
  change,
}: {
  cloudProvider: CloudProvider | null | undefined;
  change: ConfigControlSpec['change'];
}): ConfigChangeCapability | null => {
  if (!cloudProvider) {
    return null;
  }
  return CONFIG_CHANGE_MATRIX[cloudProvider][change] ?? null;
};

export const isConfigChangeEnforceable = ({
  cloudProvider,
  change,
}: {
  cloudProvider: CloudProvider | null | undefined;
  change: ConfigControlSpec['change'];
}): boolean =>
  resolveConfigChangeCapability({ cloudProvider, change })?.support ===
  CONFIG_CHANGE_SUPPORT.SUPPORTED;

export const listEnforceableConfigChanges = (): ReadonlyArray<{
  cloudProvider: CloudProvider;
  change: ConfigControlSpec['change'];
}> =>
  Object.entries(CONFIG_CHANGE_MATRIX).flatMap(([cloudProvider, changes]) =>
    Object.entries(changes).flatMap(([change, capability]) =>
      capability.support === CONFIG_CHANGE_SUPPORT.SUPPORTED
        ? [
            {
              cloudProvider: cloudProvider as CloudProvider,
              change: change as ConfigControlSpec['change'],
            },
          ]
        : [],
    ),
  );

export const buildConfigChangeConstraints = ({
  cloudProvider,
  supportedChanges,
}: {
  cloudProvider: CloudProvider | null;
  supportedChanges?: readonly ConfigControlSpec['change'][];
}): string[] => {
  if (!cloudProvider) {
    return [
      'Cloud provider could not be determined; prefer advisory change types with explicit target identifiers.',
    ];
  }
  const changes =
    supportedChanges ??
    (['disablePublicAccess', 'enforceTls', 'dnsRecord', 'emailAuth'] as const);
  return changes.flatMap((change) => {
    const capability = resolveConfigChangeCapability({ cloudProvider, change });
    if (!capability) {
      return [];
    }
    return [`${change}: ${capability.support}`, ...capability.constraints];
  });
};

export const collectConfigChangeCapabilityViolations = ({
  spec,
  cloudProvider,
}: {
  spec: PreventionControlSpec;
  cloudProvider: CloudProvider | null | undefined;
}): string[] => {
  if (spec.controlType !== PREVENTION_CONTROL_TYPES.CONFIG) {
    return [];
  }
  const violations: string[] = [];
  if (!spec.target.trim()) {
    violations.push('Config prevention requires a non-empty target.');
  }
  const capability = resolveConfigChangeCapability({
    cloudProvider,
    change: spec.change,
  });
  if (!capability) {
    violations.push(
      `Config change "${spec.change}" is not mapped for cloud provider ${cloudProvider ?? '(unknown)'}.`,
    );
    return violations;
  }
  if (capability.support === CONFIG_CHANGE_SUPPORT.UNSUPPORTED) {
    violations.push(
      `Config change "${spec.change}" is unsupported for ${cloudProvider}.`,
    );
  }
  if (
    !cloudProvider ||
    capability.support !== CONFIG_CHANGE_SUPPORT.SUPPORTED
  ) {
    return violations;
  }
  return [
    ...violations,
    ...collectEnforcedSpecViolations({ spec, cloudProvider }),
  ];
};

const ENFORCED_DETAIL_REQUIREMENTS: Record<
  string,
  { fields: readonly string[]; message: string }
> = {
  'aws:enforceTls': {
    fields: ['listenerArn'],
    message: 'AWS enforceTls requires listenerArn in details.',
  },
  'aws:dnsRecord': {
    fields: ['hostedZoneId', 'recordType'],
    message: 'AWS dnsRecord requires hostedZoneId and recordType in details.',
  },
  'aws:emailAuth': {
    fields: ['hostedZoneId'],
    message: 'AWS emailAuth requires hostedZoneId in details.',
  },
  'azure:disablePublicAccess': {
    fields: ['resourceGroup'],
    message: 'Azure disablePublicAccess requires resourceGroup in details.',
  },
};

const collectEnforcedSpecViolations = ({
  spec,
  cloudProvider,
}: {
  spec: ConfigControlSpec;
  cloudProvider: CloudProvider;
}): string[] => {
  const violations: string[] = [];

  if (spec.change === 'disablePublicAccess' && cloudProvider === 'aws') {
    if (!readBucketName(spec)) {
      violations.push(
        'AWS disablePublicAccess requires bucketName in details or a bucket-shaped target.',
      );
    }
  }

  if (
    (spec.change === 'dnsRecord' || spec.change === 'emailAuth') &&
    cloudProvider === 'aws' &&
    !hasRecordValue(spec)
  ) {
    violations.push(
      `AWS ${spec.change} requires recordValue or recordValues in details.`,
    );
  }

  const requirement =
    ENFORCED_DETAIL_REQUIREMENTS[`${cloudProvider}:${spec.change}`];
  if (
    requirement &&
    requirement.fields.some((field) => !readDetailString(spec, field))
  ) {
    violations.push(requirement.message);
  }

  return violations;
};

const hasRecordValue = (spec: ConfigControlSpec): boolean => {
  if (readDetailString(spec, 'recordValue')) {
    return true;
  }
  const values = spec.details?.recordValues;
  return (
    Array.isArray(values) &&
    values.some((value) => typeof value === 'string' && value.trim().length > 0)
  );
};

const readDetailString = (
  spec: ConfigControlSpec,
  field: string,
): string | null => {
  const value = spec.details?.[field];
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null;
};

const readBucketName = (spec: ConfigControlSpec): string | null => {
  const fromDetails = spec.details?.bucketName;
  if (typeof fromDetails === 'string' && fromDetails.trim().length > 0) {
    return fromDetails.trim();
  }
  const target = spec.target.trim();
  if (/^arn:aws:s3:::[^/]+$/i.test(target)) {
    return target.split(':::')[1]?.split('/')[0] ?? null;
  }
  if (/^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/.test(target)) {
    return target;
  }
  return null;
};

export const isConfigChangeCapabilityViolationError = (
  error: unknown,
): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  const message = error.message.toLowerCase();
  return (
    message.includes('config change') || message.includes('config prevention')
  );
};
